// TanStack Query hooks for every backend endpoint. Pages import from here —
// nothing else calls `api.*` directly.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  Service, OverviewSummary, Deployment, Pod, Trace, Span, LogLine, LogCluster,
  ServiceMetrics, AlertRule, Incident, DependencyGraph, HealthReport,
  Certificate, Secret, NetworkPolicy, VulnerabilityFinding, SBOMComponent,
  FlameNode, HotMethod, SlowQuery, RCACause, AIAnswer, ServiceCreateInput, User,
  Project, ProjectCreateInput, ProjectGroup, ProjectGroupCreateInput,
  ProjectMember, ProjectMemberInput, ProjectsSummary,
} from "@coremonit/types";

// ─── Auth ────────────────────────────────────────────────────────────────
export const useMe = () =>
  useQuery({ queryKey: ["me"], queryFn: () => api.get<{ user: User }>("/auth/me") });

// ─── Services / Overview ─────────────────────────────────────────────────
export const useServices = (params: { q?: string; status?: string; env?: string; team?: string; tag?: string; project?: string; group?: string } = {}) =>
  useQuery({
    queryKey: ["services", params],
    queryFn: () => api.get<{ items: Service[]; total: number }>("/services", params as Record<string, string>),
  });

export const useService = (id: string | undefined) =>
  useQuery({
    queryKey: ["service", id],
    queryFn: () => api.get<Service>(`/services/${id}`),
    enabled: !!id,
    refetchInterval: 15_000,
  });

export const useOverview = () =>
  useQuery({
    queryKey: ["overview"],
    queryFn: () => api.get<OverviewSummary>("/services/summary"),
    refetchInterval: 15_000,
  });

export const useCreateService = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ServiceCreateInput) => api.post<{ service: Service }>("/services", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["services"] }),
  });
};

export const useValidateConnection = (id: string) =>
  useMutation({ mutationFn: () => api.post<{ checks: Array<{ id: string; label: string; status: string; latencyMs: number; message: string }> }>(`/services/${id}/validate-connection`) });

export const useCollectBaseline = (id: string) =>
  useMutation({ mutationFn: () => api.post<{ window: string; baseline: Record<string, number> }>(`/services/${id}/collect-baseline`) });

// ─── Deployments ─────────────────────────────────────────────────────────
export const useDeployments = () =>
  useQuery({ queryKey: ["deployments"], queryFn: () => api.get<{ items: Deployment[] }>("/deployments") });

export const useServiceDeployments = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["deployments", serviceId],
    queryFn: () => api.get<{ items: Deployment[] }>(`/deployments/service/${serviceId}`),
    enabled: !!serviceId,
  });

export const useDeploymentDiff = (id: string | undefined) =>
  useQuery({
    queryKey: ["deployment-diff", id],
    queryFn: () => api.get<{ diff: Array<{ op: string; line: string }> }>(`/deployments/${id}/diff`),
    enabled: !!id,
  });

export const useRollback = () =>
  useMutation({ mutationFn: (id: string) => api.post<{ ok: boolean; message: string }>(`/deployments/${id}/rollback`) });

export const useRestart = () =>
  useMutation({ mutationFn: (id: string) => api.post<{ ok: boolean }>(`/deployments/${id}/restart`) });

export const useRedeploy = () =>
  useMutation({ mutationFn: (id: string) => api.post<{ ok: boolean }>(`/deployments/${id}/redeploy`) });

// ─── Containers ──────────────────────────────────────────────────────────
export const useServicePods = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["pods", serviceId],
    queryFn: () => api.get<{ pods: Pod[] }>(`/containers/service/${serviceId}`),
    enabled: !!serviceId,
  });

// ─── Traces / Logs / Metrics ─────────────────────────────────────────────
export const useTraces = (q: { service?: string; q?: string; onlyErrors?: boolean; limit?: number } = {}) =>
  useQuery({
    queryKey: ["traces", q],
    queryFn: () => api.get<{ items: Trace[]; total: number }>("/traces", {
      service: q.service, q: q.q, onlyErrors: q.onlyErrors ? "true" : undefined, limit: q.limit,
    }),
  });

export const useTrace = (id: string | undefined) =>
  useQuery({
    queryKey: ["trace", id],
    queryFn: () => api.get<{ trace: Trace; spans: Span[] }>(`/traces/${id}`),
    enabled: !!id,
  });

export const useTraceAISummary = () =>
  useMutation({ mutationFn: (id: string) => api.post<{ summary: string; criticalPath: string[]; suggestedActions: string[] }>(`/traces/${id}/ai-summary`) });

export const useLogs = (q: { service?: string; q?: string; regex?: string; level?: string; limit?: number } = {}) =>
  useQuery({
    queryKey: ["logs", q],
    queryFn: () => api.get<{ items: LogLine[]; total: number }>("/logs", q as Record<string, string>),
  });

export const useLogClusters = () =>
  useQuery({ queryKey: ["log-clusters"], queryFn: () => api.get<{ items: LogCluster[] }>("/logs/clusters") });

export const useLogsAISummary = () =>
  useQuery({ queryKey: ["logs-ai"], queryFn: () => api.get<{ summary: string; top: string[] }>("/logs/ai-summary") });

export const useServiceMetrics = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["metrics", serviceId],
    queryFn: () => api.get<ServiceMetrics>(`/metrics/service/${serviceId}`),
    enabled: !!serviceId,
    refetchInterval: 30_000,
  });

// ─── Alerts / RCA ────────────────────────────────────────────────────────
export const useAlertRules  = () => useQuery({ queryKey: ["alert-rules"],  queryFn: () => api.get<{ items: AlertRule[] }>("/alerts/rules") });
export const useIncidents   = () => useQuery({ queryKey: ["incidents"],    queryFn: () => api.get<{ items: Incident[] }>("/alerts/incidents"), refetchInterval: 20_000 });
export const useRCA = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["rca", serviceId],
    queryFn: () => api.get<{ causes: RCACause[] }>(`/alerts/rca/${serviceId}`),
    enabled: !!serviceId,
  });

// ─── Dependencies ────────────────────────────────────────────────────────
export const useDependencyGraph = () =>
  useQuery({ queryKey: ["graph"], queryFn: () => api.get<DependencyGraph>("/dependencies") });

export const useServiceDependencies = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["deps", serviceId],
    queryFn: () => api.get<{
      service: DependencyGraph["services"][number];
      upstream: DependencyGraph["services"];
      downstream: DependencyGraph["services"];
      critical: DependencyGraph["edges"];
      blastRadius: number;
      circular: string[];
    }>(`/dependencies/service/${serviceId}`),
    enabled: !!serviceId,
  });

// ─── Health ──────────────────────────────────────────────────────────────
export const useServiceHealth = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["health", serviceId],
    queryFn: () => api.get<HealthReport>(`/health/service/${serviceId}`),
    enabled: !!serviceId,
  });

// ─── Security ────────────────────────────────────────────────────────────
export const useSecuritySummary   = () => useQuery({ queryKey: ["sec-summary"], queryFn: () => api.get<Record<string, unknown>>("/security/summary") });
export const useCertificates      = () => useQuery({ queryKey: ["certs"],       queryFn: () => api.get<{ items: Certificate[] }>("/security/certificates") });
export const useSecrets           = () => useQuery({ queryKey: ["secrets"],     queryFn: () => api.get<{ items: Secret[] }>("/security/secrets") });
export const useNetworkPolicies   = () => useQuery({ queryKey: ["netpols"],     queryFn: () => api.get<{ items: NetworkPolicy[] }>("/security/network-policies") });
export const useVulnerabilities   = () => useQuery({ queryKey: ["vulns"],       queryFn: () => api.get<{ items: VulnerabilityFinding[] }>("/security/vulnerabilities") });
export const useSBOM              = () => useQuery({ queryKey: ["sbom"],        queryFn: () => api.get<{ items: SBOMComponent[] }>("/security/sbom") });

// ─── Profiling ───────────────────────────────────────────────────────────
export const useFlamegraph = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["flame", serviceId],
    queryFn: () => api.get<{ flamegraph: FlameNode }>(`/profiling/flamegraph/${serviceId}`),
    enabled: !!serviceId,
  });
export const useHotMethods = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["hot", serviceId],
    queryFn: () => api.get<{ items: HotMethod[] }>(`/profiling/hot-methods/${serviceId}`),
    enabled: !!serviceId,
  });
export const useSlowQueries = () =>
  useQuery({ queryKey: ["slow-queries"], queryFn: () => api.get<{ items: SlowQuery[] }>("/profiling/slow-queries") });
export const useHeapAnalysis = () =>
  useQuery({ queryKey: ["heap"], queryFn: () => api.get<{ totalMb: number; breakdown: Array<{ label: string; mb: number }>; largestObjects: Array<{ class: string; count: number; mb: number }> }>("/profiling/heap-analysis") });
export const useAsyncWait = () =>
  useQuery({ queryKey: ["async-wait"], queryFn: () => api.get<{ items: Array<{ name: string; waitMs: number; samples: number }> }>("/profiling/async-wait") });

// ─── AI / Testing ────────────────────────────────────────────────────────
export const useAskAI = () =>
  useMutation({ mutationFn: (input: { text: string; scopeServiceId?: string }) => api.post<AIAnswer>("/ai/ask", input) });

export const useRunTest = () =>
  useMutation({ mutationFn: (input: { serviceId: string; kind: string }) => api.post<{
    testId: string; kind: string; passed: boolean; durationMs: number;
    checks: Array<{ id: string; label: string; status: string; durationMs: number }>;
    metrics: { p95: number; p99: number; throughputRps: number; errorRate: number };
  }>("/testing/run", input) });

// ─── Projects / Groups / Members ─────────────────────────────────────────
export const useProjects = () =>
  useQuery({ queryKey: ["projects"], queryFn: () => api.get<{ items: Project[]; total: number }>("/projects") });

export const useProjectsSummary = () =>
  useQuery({
    queryKey: ["projects-summary"],
    queryFn: () => api.get<ProjectsSummary>("/projects/summary"),
    refetchInterval: 30_000,
  });

export const useProject = (id: string | undefined) =>
  useQuery({
    queryKey: ["project", id],
    queryFn: () => api.get<Project>(`/projects/${id}`),
    enabled: !!id,
  });

export const useCreateProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectCreateInput) => api.post<{ project: Project }>("/projects", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["projects-summary"] });
    },
  });
};

export const useUpdateProject = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Pick<Project, "name" | "description" | "color" | "icon" | "ownerEmail">>) =>
      api.patch<{ project: Project }>(`/projects/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["projects-summary"] });
    },
  });
};

export const useDeleteProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/projects/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["projects-summary"] });
    },
  });
};

export const useProjectServices = (id: string | undefined, groupId?: string) =>
  useQuery({
    queryKey: ["project-services", id, groupId],
    queryFn: () => api.get<{ items: Service[]; total: number }>(`/projects/${id}/services`, groupId ? { group: groupId } : undefined),
    enabled: !!id,
  });

export const useProjectGroups = (id: string | undefined) =>
  useQuery({
    queryKey: ["project-groups", id],
    queryFn: () => api.get<{ items: ProjectGroup[] }>(`/projects/${id}/groups`),
    enabled: !!id,
  });

export const useCreateProjectGroup = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectGroupCreateInput) => api.post<{ group: ProjectGroup }>(`/projects/${projectId}/groups`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-groups", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
};

export const useDeleteProjectGroup = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => api.del<{ ok: boolean }>(`/projects/${projectId}/groups/${groupId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-groups", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
};

export const useProjectMembers = (id: string | undefined) =>
  useQuery({
    queryKey: ["project-members", id],
    queryFn: () => api.get<{ items: ProjectMember[] }>(`/projects/${id}/members`),
    enabled: !!id,
  });

export const useAddProjectMember = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectMemberInput) => api.post<{ member: ProjectMember }>(`/projects/${projectId}/members`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-members", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
};

export const useUpdateProjectMember = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role: ProjectMember["role"] }) =>
      api.patch<{ member: ProjectMember }>(`/projects/${projectId}/members/${input.userId}`, { role: input.role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-members", projectId] }),
  });
};

export const useRemoveProjectMember = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.del<{ ok: boolean }>(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-members", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
};

// ─── Infrastructure hierarchy / clusters / search ────────────────────────
export const useInfrastructureTree = (preset?: string) =>
  useQuery({
    queryKey: ["infrastructure-tree", preset],
    queryFn: () => api.get<import("@coremonit/types").InfrastructureTree>("/infrastructure/tree", preset ? { preset } : undefined),
    staleTime: 60_000,
  });

export const useCluster = (id: string | undefined) =>
  useQuery({
    queryKey: ["cluster", id],
    queryFn: () => api.get<import("@coremonit/types").ClusterSummary>(`/clusters/${id}`),
    enabled: !!id,
    refetchInterval: 30_000,
  });

export const useClusterTopology = (id: string | undefined) =>
  useQuery({
    queryKey: ["cluster-topology", id],
    queryFn: () => api.get<import("@coremonit/types").ClusterTopology>(`/clusters/${id}/topology`),
    enabled: !!id,
  });

export const useClusterK8sObjects = (id: string | undefined, kind?: string) =>
  useQuery({
    queryKey: ["cluster-k8s", id, kind],
    queryFn: () => api.get<{ items: import("@coremonit/types").K8sObjectSummary[]; total: number }>(`/clusters/${id}/objects`, kind ? { kind } : undefined),
    enabled: !!id,
  });

export const useDeploymentTimeline = (serviceId: string | undefined) =>
  useQuery({
    queryKey: ["deployment-timeline", serviceId],
    queryFn: () => api.get<{ items: import("@coremonit/types").DeploymentTimelineEntry[] }>(`/services/${serviceId}/deployment-timeline`),
    enabled: !!serviceId,
  });

export const useContainerDetail = (serviceId: string | undefined, podId: string | undefined, containerName: string | undefined) =>
  useQuery({
    queryKey: ["container-detail", serviceId, podId, containerName],
    queryFn: () => api.get<import("@coremonit/types").ContainerDetail>(`/containers/${serviceId}/${podId}/${encodeURIComponent(containerName!)}`),
    enabled: !!(serviceId && podId && containerName),
    refetchInterval: 15_000,
  });

export const useGlobalSearch = (q: string, enabled = true) =>
  useQuery({
    queryKey: ["global-search", q],
    queryFn: () => api.get<import("@coremonit/types").GlobalSearchResponse>("/search", { q }),
    enabled: enabled && q.trim().length >= 2,
  });
